import { useUserEventsByTypesStream } from '@/api';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Modal, Tabs } from 'antd';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CloseIcon } from '../Common/Icons';
import './Wellness.less';
import WellnessHistoryInsightChart from './WellnessHistoryInsightChart';
import WellnessHistoryTableData from './WellnessHistoryTableData';
import WellnessIconByTitle from './WellnessIconByTitle';
import { unitTitleData } from './WellnessUtils';

// Table Data tab: streams the reading history of one biometric type, so rows appear as they
// arrive (newest first). Mounted only while the modal is open, so closing it aborts the download.
const WellnessHistoryTable = ({
  useruuid,
  eventType,
  historyDays,
  formatEvents,
  ...tableProps
}) => {
  const { data, isLoading, isStreaming, isPartial } = useUserEventsByTypesStream({
    useruuid,
    eventTypes: eventType,
    // Whole UTC days: from the start of the first day to the end of today.
    startDate: moment.utc().subtract(historyDays, 'days').startOf('day'),
    endDate: moment.utc().endOf('day'),
  });

  return (
    <WellnessHistoryTableData
      {...tableProps}
      modalLoading={isLoading}
      modalData={formatEvents(data)}
      isStreaming={isStreaming}
      isPartial={isPartial}
    />
  );
};

WellnessHistoryTable.propTypes = {
  useruuid: PropTypes.string,
  eventType: PropTypes.string,
  historyDays: PropTypes.number.isRequired,
  formatEvents: PropTypes.func.isRequired,
};

const WellnessHistoryModal = ({
  showHistoryModal,
  closeHistoryModal,
  useruuid,
  eventType,
  historyDays,
  formatHistoryEvents,
  modalTitle,
  userMeasurement,
  deviceObj,
  timezone,
  selectedRange,
  setSelectedRange,
  is24hoursformat,
  userDefinedMeasurement,
  insightStartDateTime,
  insightEndDateTime,
  insightData,
  insightLoading,
  getTranslatedLabel,
}) => {
  const trans = useSelector(translator);
  return (
    <Modal
      open={showHistoryModal}
      onOk={closeHistoryModal}
      onCancel={closeHistoryModal}
      footer={null}
      closable={false}
      destroyOnHidden
      height={722}
      width={1472}
      className="wellness-modal-container"
    >
      <div className="modal-container">
        {/* Header Section */}
        <div className="wellness-modal-header-container">
          <div className="wellness-header-icon-title-new-container">
            <div className="wellness-header-icon-new">
              <WellnessIconByTitle title={modalTitle} style={{ width: '55px', height: '55px' }} />
            </div>
            <div className="wellness-modal-header-info">
              <div className="wellness-header-title-new">
                {trans(
                  String(unitTitleData['device_title'][modalTitle])
                    .split(' ')
                    .join('')
                    .toUpperCase()
                )}{' '}
                {trans('HISTORY')}
              </div>
              <div className="wellness-header-sub-title">{trans('HEALTH_READING_L')}</div>
            </div>
          </div>
          <div className="close-button" onClick={closeHistoryModal}>
            <CloseIcon className="close-icon" />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container">
          <Tabs
            defaultActiveKey="1"
            className="wellness-tabs"
            tabPosition="top"
            items={[
              {
                label: trans('TABLEDATA'),
                key: '1',
                children: (
                  <WellnessHistoryTable
                    useruuid={useruuid}
                    eventType={eventType}
                    historyDays={historyDays}
                    formatEvents={formatHistoryEvents}
                    userMeasurement={userMeasurement}
                    deviceObj={deviceObj}
                    getTranslatedLabel={getTranslatedLabel}
                  />
                ),
              },
              {
                label: trans('INSIGHTSANDCHART'),
                key: '2',
                children: (
                  <WellnessHistoryInsightChart
                    userMeasurement={userMeasurement}
                    modalTitle={modalTitle}
                    timezone={timezone}
                    selectedRange={selectedRange}
                    setSelectedRange={setSelectedRange}
                    is24hoursformat={is24hoursformat}
                    userDefinedMeasurement={userDefinedMeasurement}
                    insightStartDateTime={insightStartDateTime}
                    insightEndDateTime={insightEndDateTime}
                    insightData={insightData}
                    insightLoading={insightLoading}
                    getTranslatedLabel={getTranslatedLabel}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
};

WellnessHistoryModal.propTypes = {
  showHistoryModal: PropTypes.bool,
  closeHistoryModal: PropTypes.func,
  useruuid: PropTypes.string,
  eventType: PropTypes.string,
  historyDays: PropTypes.number,
  formatHistoryEvents: PropTypes.func,
  modalTitle: PropTypes.string,
  userMeasurement: PropTypes.string,
  deviceObj: PropTypes.object,
  timezone: PropTypes.string,
  is24hoursformat: PropTypes.bool,
  selectedRange: PropTypes.string,
  setSelectedRange: PropTypes.func,
  userDefinedMeasurement: PropTypes.string,
  insightStartDateTime: PropTypes.string,
  insightEndDateTime: PropTypes.string,
  insightData: PropTypes.array,
  insightLoading: PropTypes.bool,
  getTranslatedLabel: PropTypes.func,
};

export default WellnessHistoryModal;
